import { defineMessages } from 'react-intl'

import { ReportChartMetrics } from '@webapp/modules/reports/entities/enums/ReportChartMetricEnum'
import { type ReportConfig } from './ReportConfig'

const scope = `procurify.modules.reports.config.reportConfigs`

const messages = defineMessages({
  vendorReportTitle: {
    id: `${scope}.vendorReportTitle`,
    defaultMessage: 'Orders by vendor',
  },
  userReportTitle: {
    id: `${scope}.userReportTitle`,
    defaultMessage: 'Orders by user',
  },
  secondaryCategoryReportTitle: {
    id: `${scope}.secondaryCategoryReportTitle`,
    defaultMessage: 'Orders by department',
  },
  expenseReportTitle: {
    id: `${scope}.expenseReportTitle`,
    defaultMessage: 'Expenses by department',
  },
  ordersSectionHeading: {
    id: `${scope}.ordersSectionHeading`,
    defaultMessage: 'Orders',
  },
  expensesSectionHeading: {
    id: `${scope}.expensesSectionHeading`,
    defaultMessage: 'Expenses',
  },
  exportOrdersButtonLabel: {
    id: `${scope}.exportOrdersButtonLabel`,
    defaultMessage: 'Export orders',
  },
  exportExpenseItemsButtonLabel: {
    id: `${scope}.exportExpenseItemsButtonLabel`,
    defaultMessage: 'Export expense items',
  },
  orderItemsExportTypeLabel: {
    id: `${scope}.orderItemsExportTypeLabel`,
    defaultMessage: 'Order items',
  },
  expenseItemsExportTypeLabel: {
    id: `${scope}.expenseItemsExportTypeLabel`,
    defaultMessage: 'Expense items',
  },
  topVendorsChartHeading: {
    id: `${scope}.topVendorsChartHeading`,
    defaultMessage: 'Top vendors',
  },
  topSpendChartHeading: {
    id: `${scope}.topSpendChartHeading`,
    defaultMessage: 'Top spend',
  },
  topDepartmentsChartHeading: {
    id: `${scope}.topDepartmentsChartHeading`,
    defaultMessage: 'Top departments',
  },
  totalSpendingTileLabel: {
    id: `${scope}.totalSpendingTileLabel`,
    defaultMessage: 'Total spending',
  },
  totalVendorsUsedTileLabel: {
    id: `${scope}.totalVendorsUsedTileLabel`,
    defaultMessage: 'Total vendors used',
  },
  totalItemsPurchasedTileLabel: {
    id: `${scope}.totalItemsPurchasedTileLabel`,
    defaultMessage: 'Total items purchased',
  },
  spendByUserTileLabel: {
    id: `${scope}.spendByUserTileLabel`,
    defaultMessage: 'Spend by user',
  },
  totalDepartmentUsageTileLabel: {
    id: `${scope}.totalDepartmentUsageTileLabel`,
    defaultMessage: 'Total department usage',
  },
  totalReimbursementTileLabel: {
    id: `${scope}.totalReimbursementTileLabel`,
    defaultMessage: 'Total reimbursement',
  },
  totalExpenditureTileLabel: {
    id: `${scope}.totalExpenditureTileLabel`,
    defaultMessage: 'Total expenditure',
  },
})

// Copy per BHV-013 (vendor tiles), BHV-017 ("Top vendors" heading).
export const vendorReportConfig: ReportConfig = {
  key: 'vendor',
  kind: 'orders',
  dimension: 'vendors',
  titleKey: messages.vendorReportTitle,
  sectionHeadingKey: messages.ordersSectionHeading,
  exportButtonLabelKey: messages.exportOrdersButtonLabel,
  exportTypeLabelKey: messages.orderItemsExportTypeLabel,
  chartHeadingKey: messages.topVendorsChartHeading,
  tiles: [
    {
      labelKey: messages.totalSpendingTileLabel,
      metric: ReportChartMetrics.SPENT,
      chartType: 'line',
      format: 'currency',
    },
    {
      labelKey: messages.totalVendorsUsedTileLabel,
      metric: ReportChartMetrics.USAGE,
      chartType: 'bar',
      format: 'usage',
    },
    {
      labelKey: messages.totalItemsPurchasedTileLabel,
      metric: ReportChartMetrics.COUNT,
      chartType: 'line',
      format: 'count',
    },
  ],
  defaultDetailMetric: ReportChartMetrics.SPENT,
  aggregateDefaultSort: '-spent',
  detailDefaultSort: '-account__account_code__code',
  detailExportEntityKey: 'po_vendor',
  aggregatePage: 'OrdersByVendorReportPage',
  detailPage: 'OrdersByVendorReportDetailsPage',
  referrerType: 'orders.vendor',
  paginationIds: {
    aggregate: 'REPORT_VENDOR_SPEND',
    lineItems: 'REPORT_ORDER_LINE_ITEMS',
  },
}

// Copy per BHV-014 (user tiles; "Orders by user" sentence-cased fix).
export const userReportConfig: ReportConfig = {
  key: 'user',
  kind: 'orders',
  dimension: 'users',
  titleKey: messages.userReportTitle,
  sectionHeadingKey: messages.ordersSectionHeading,
  exportButtonLabelKey: messages.exportOrdersButtonLabel,
  exportTypeLabelKey: messages.orderItemsExportTypeLabel,
  chartHeadingKey: messages.topSpendChartHeading,
  tiles: [
    {
      labelKey: messages.totalSpendingTileLabel,
      metric: ReportChartMetrics.SPENT,
      chartType: 'line',
      format: 'currency',
    },
    {
      labelKey: messages.totalItemsPurchasedTileLabel,
      metric: ReportChartMetrics.COUNT,
      chartType: 'line',
      format: 'count',
    },
    {
      labelKey: messages.spendByUserTileLabel,
      metric: ReportChartMetrics.USAGE,
      chartType: 'bar',
      format: 'usage',
    },
  ],
  defaultDetailMetric: ReportChartMetrics.USAGE,
  aggregateDefaultSort: '-spent',
  detailDefaultSort: '-total_cost',
  detailExportEntityKey: 'user',
  aggregatePage: 'OrdersByUserReportPage',
  detailPage: 'OrdersByUserReportDetailsPage',
  referrerType: 'orders.user',
  paginationIds: {
    aggregate: 'REPORT_USER_SPEND',
    lineItems: 'REPORT_ORDER_LINE_ITEMS',
  },
}

// Copy per BHV-015 (secondary-category tiles), BHV-017 ("Top spend").
export const secondaryCategoryReportConfig: ReportConfig = {
  key: 'secondaryCategory',
  kind: 'orders',
  dimension: 'departments',
  titleKey: messages.secondaryCategoryReportTitle,
  sectionHeadingKey: messages.ordersSectionHeading,
  exportButtonLabelKey: messages.exportOrdersButtonLabel,
  exportTypeLabelKey: messages.orderItemsExportTypeLabel,
  chartHeadingKey: messages.topSpendChartHeading,
  tiles: [
    {
      labelKey: messages.totalSpendingTileLabel,
      metric: ReportChartMetrics.SPENT,
      chartType: 'line',
      format: 'currency',
    },
    {
      labelKey: messages.totalDepartmentUsageTileLabel,
      metric: ReportChartMetrics.USAGE,
      chartType: 'bar',
      format: 'usage',
    },
    {
      labelKey: messages.totalItemsPurchasedTileLabel,
      metric: ReportChartMetrics.COUNT,
      chartType: 'line',
      format: 'count',
    },
  ],
  defaultDetailMetric: ReportChartMetrics.SPENT,
  aggregateDefaultSort: '-spent',
  detailDefaultSort: '-account__account_code__code',
  detailExportEntityKey: 'department',
  aggregatePage: 'OrdersBySecondaryCategoryReportPage',
  detailPage: 'OrdersBySecondaryCategoryReportDetailsPage',
  referrerType: 'orders.department',
  paginationIds: {
    aggregate: 'REPORT_SECONDARY_CATEGORY_SPEND',
    lineItems: 'REPORT_ORDER_LINE_ITEMS',
  },
}

// Copy per BHV-016 (expense tiles + "Export expense items"), BHV-017
// ("Top departments" — fixes the Angular "TOP VENDORS" mis-title).
export const expenseReportConfig: ReportConfig = {
  key: 'expense',
  kind: 'expenses',
  titleKey: messages.expenseReportTitle,
  sectionHeadingKey: messages.expensesSectionHeading,
  exportButtonLabelKey: messages.exportExpenseItemsButtonLabel,
  exportTypeLabelKey: messages.expenseItemsExportTypeLabel,
  chartHeadingKey: messages.topDepartmentsChartHeading,
  tiles: [
    {
      labelKey: messages.totalReimbursementTileLabel,
      metric: ReportChartMetrics.REIMBURSABLE,
      chartType: 'line',
      format: 'currency',
    },
    {
      labelKey: messages.totalExpenditureTileLabel,
      metric: ReportChartMetrics.SPENT,
      chartType: 'line',
      format: 'currency',
    },
    {
      labelKey: messages.totalDepartmentUsageTileLabel,
      metric: ReportChartMetrics.USAGE,
      chartType: 'bar',
      format: 'usage',
    },
  ],
  defaultDetailMetric: ReportChartMetrics.SPENT,
  aggregateDefaultSort: '-spent',
  detailDefaultSort: '-account__account_code__code',
  detailExportEntityKey: 'department',
  aggregatePage: 'ExpensesBySecondaryCategoryReportPage',
  detailPage: 'ExpensesBySecondaryCategoryReportDetailsPage',
  referrerType: 'expenses.department',
  paginationIds: {
    aggregate: 'REPORT_EXPENSE_SPEND',
    lineItems: 'REPORT_EXPENSE_LINE_ITEMS',
  },
}
